import React, { useEffect, useState, useContext } from 'react';
import { Sidebar, Menu, MenuItem, SubMenu, menuClasses } from 'react-pro-sidebar';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { TbChevronsLeft, TbChevronsRight } from 'react-icons/tb';
import { GiHamburgerMenu } from 'react-icons/gi';
import Tooltip from '@mui/material/Tooltip';
import { useGeneralInfoStore } from '../stores/GeneralInfoStore';
import { ThemeContext } from '../stores/ThemeContext';
import { getTheme } from '../assets/styles/Theme';
import { useTranslation } from 'react-i18next';
import appRoutesConfig from '../config/appConfig';
import { useTranslatedTilesData, getIconForTile, renderIcon } from '../config/tilesConfig';
import { categoryTabs } from '../config/categoryTabsCofig';
import CloudDownload from '../assets/icons/demoIcons/cloud-download';
import Help from '../assets/icons/help';
import Datenschutz from '../assets/icons/datenschutz';
import Impressum from '../assets/icons/impressum';
import CookieIcon from '@mui/icons-material/Cookie';
import UIWhite from '../assets/img/logo-ui-square-white.png';
import UIDark from '../assets/img/logo-ui-square-dark.png';
import { demoMode, impressumUrl, datenschutzUrl } from '../config/config';
import { isActiveRoute } from '../utils/util';
import { useUserStore } from '../stores/userStore';

const MenuCloseItem = styled(MenuItem)`
  a {
    span { margin: auto; }
    span:nth-child(2) { display: none; }
  }
`;

const StyledMenuContent = styled('div')`
  flex: 1;
  margin-top: 2.5rem;
  overflow-y: auto;
  max-height: calc(100vh - 260px);
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar { display: none; }
`;

const MyDiv = styled.div`
  @media (max-width: 800px) {
    margin-top: 2rem;
    margin-bottom: 3rem;
  }
`;

type SidebarComponentProps = { isSidebarOpen: boolean };

const SidebarComponent: React.FC<SidebarComponentProps> = ({ isSidebarOpen }) => {
    const navigate = useNavigate();
    const { darkMode } = useContext(ThemeContext);
    const theme = getTheme(darkMode);
    const logoColor = theme.palette.customText.logo;
    const { t } = useTranslation();
    const translatedTiles = useTranslatedTilesData();
    const { user } = useUserStore();
    const userRoles: string[] = user?.roles || [];

    const {
        category,
        setCategory,
        activeTab,
        setActiveTab,
        setCurrentPage,
        setIsCookieBannerVisible,
        isCookieBannerVisible
    } = useGeneralInfoStore();

    const [routesConfig, setRoutesConfig] = useState(appRoutesConfig.Pages);
    const [collapsed, setCollapsed] = useState(true);


    // Determine which categories have enabled pages (considering role filtering)
    const getActiveCategories = React.useCallback(() => {
        return ['traffic', 'environment', 'energy', 'utility']
            .filter(cat => {
                if (demoMode) {
                    // In demo mode, show all categories that have tiles
                    return translatedTiles.some(tile => tile.categories.includes(cat));
                } else {
                    // In production mode, show categories that have tabs matching user roles
                    const tabsArray = categoryTabs[cat] || [];
                    const hasMatchingRoles = tabsArray.some(tab => userRoles.includes(tab.name));
                    return hasMatchingRoles;
                }
            });
    }, [translatedTiles, userRoles]);

    // Handle initial and subsequent category/tab logic
    useEffect(() => {
        const activeCats = getActiveCategories();

        // If no category yet and exactly one choice -> pick it
        if (!category && activeCats.length === 1) {
            setCategory(activeCats[0]);
            return;
        }

        // If current category invalid -> pick first valid
        if (category && !activeCats.includes(category)) {
            setCategory(activeCats[0] || '');
            return;
        }

        // Ensure activeTab is valid for the category
        if (category) {
            const tabs = categoryTabs[category] || [];
            // Filter tabs based on demo mode and user roles
            const availableTabs = demoMode
                ? tabs
                : tabs.filter(tab => {
                    const hasRole = userRoles.includes(tab.name);
                    return hasRole;
                });

            const validNames = availableTabs.map(t => t.name);
            if (!validNames.includes(activeTab) && availableTabs.length) {
                const defaultTab = availableTabs[0];
                setActiveTab(defaultTab.name);

                // Navigate to default tile for this tab
                const defaultTile = translatedTiles.find(tile =>
                    tile.id === defaultTab.defaultTileId && routesConfig[tile.id]
                );
                if (defaultTile) {
                    setCurrentPage(defaultTile.title);
                    navigate(defaultTile.navigateTo);
                }
            }
        }
    }, [category, routesConfig, translatedTiles, activeTab, setCategory, setActiveTab, setCurrentPage, navigate, userRoles, getActiveCategories]);

    const toggleCollapse = () => setCollapsed(c => !c);
    const toggleCookies = () => setIsCookieBannerVisible(!isCookieBannerVisible);

    const handleCategoryClick = (cat: string) => {
        if (cat !== category) setCategory(cat);
    };

    const SidebarMenuItem = ({ tooltip, children, ...props }: any) =>
        collapsed
            ? <Tooltip title={tooltip} placement="right"><span><MenuItem {...props}>{children}</MenuItem></span></Tooltip>
            : <MenuItem {...props}>{children}</MenuItem>;

    // Render tabs for current category
    const renderTabs = () => {
        if (!category) return null;
        const tabs = categoryTabs[category] || [];

        // Filter tabs based on demo mode and user roles
        const availableTabs = demoMode
            ? tabs
            : tabs.filter(tab => {
                const hasRole = userRoles.includes(tab.name);
                return hasRole;
            });


        return availableTabs.map(tab => {
            // Find tiles that belong to this category and tab
            const pagesForTab = translatedTiles.filter(tile =>
                tile.categories.includes(category) &&
                tile.tabs?.includes(tab.name)
            );
            // In production mode, also check route config
            const pages = demoMode
                ? pagesForTab
                : pagesForTab.filter(tile => {
                    const hasRoute = routesConfig[tile.id];
                    return hasRoute;
                });

            // FOR NOW: Let's make all tabs clickable regardless of pages
            const enabled = true; // Force enable for testing
            const tile = pagesForTab.length > 0 ? pagesForTab[0] : {
                title: tab.title,
                navigateTo: `/default-${tab.name}`
            };


            return (
                <SidebarMenuItem
                    key={tab.name}
                    tooltip={t(tab.title)}
                    icon={renderIcon(tab.icon, { color: logoColor, size: 24 })}
                    active={activeTab === tab.name}
                    style={{ opacity: enabled ? 1 : 0.5 }}
                    onClick={() => {
                        setActiveTab(tab.name);
                        if (tile && tile.title) {
                            setCurrentPage(tile.title);
                        }
                        if (tile && tile.navigateTo) {
                            navigate(tile.navigateTo);
                        }
                    }}
                >
                    {t(tab.title)}
                </SidebarMenuItem>
            );
        });
    };

    // Render top-level category links if more than one and none selected
    const renderCategoryLinks = () => {
        const act = getActiveCategories();
        if (category || act.length <= 1) return null;
        return act.map(cat => (
            <SidebarMenuItem
                key={cat}
                tooltip={t(`sidebar.categories.${cat}`)}
                icon={renderIcon(getIconForTile(cat), { color: logoColor, size: 24 })}
                onClick={() => handleCategoryClick(cat)}
            >
                {t(`sidebar.categories.${cat}`)}
            </SidebarMenuItem>
        ));
    };

    if (!isSidebarOpen) return null;

    return (
        <Sidebar
            collapsed={collapsed}
            backgroundColor={theme.palette.background.sidebarBackground}
            style={{ position: 'fixed', top: 0, left: 0, height: '100vh', zIndex: 100 }}
        >
            <Menu
                closeOnClick
                menuItemStyles={{
                    button: ({ active }: { active?: boolean }) => ({
                        color: theme.palette.background.sidebarText,
                        backgroundColor: theme.palette.background.sidebarBackground,
                        borderLeft: active
                            ? `0.125rem solid ${theme.palette.background.sidebarActive}`
                            : 'none',
                        borderBottom: `0.25px solid ${theme.palette.customText.headingsGrayedOut}`
                    }),
                    subMenuContent: () => ({
                        backgroundColor: theme.palette.background.paper || (darkMode === 'dark' ? '#2d2d2d' : '#ffffff'),
                        color: darkMode === 'dark' ? '#ffffff' : '#000000',
                    }),
                }}
                rootStyles={{
                    [`.${menuClasses.button}`]: {
                        '&:hover': {
                            color: theme.palette.background.sidebarText,
                            backgroundColor: theme.palette.background.sidebarHover
                        }
                    },
                    [`.${menuClasses.subMenuContent}`]: {
                        backgroundColor: theme.palette.background.paper || (darkMode === 'dark' ? '#2d2d2d' : '#ffffff'),
                        '& .ps-menu-button': {
                            color: darkMode === 'dark' ? '#ffffff !important' : '#000000 !important',
                        },
                        '& .ps-menu-button .ps-menu-label': {
                            color: darkMode === 'dark' ? '#ffffff !important' : '#000000 !important',
                        },
                        '& .ps-menu-button span': {
                            color: darkMode === 'dark' ? '#ffffff !important' : '#000000 !important',
                        }
                    },
                    '& > ul:first-of-type': {
                        height: '100vh',
                        display: 'flex',
                        flexDirection: 'column',
                        color: theme.palette.background.sidebarBackground
                    }
                }}
            >
                <MyDiv className="d-flex align-items-center justify-content-center py-1">
                    <img
                        onClick={() => { setCategory(''); setCurrentPage(''); navigate('/Catalogue'); }}
                        alt={t('sidebar.logoAlt')}
                        width={75}
                        src={darkMode === 'dark' ? UIWhite : UIDark}
                        style={{ cursor: 'pointer' }}
                    />
                </MyDiv>

                <StyledMenuContent>
                    {renderTabs() ?? renderCategoryLinks()}
                </StyledMenuContent>

                {/* bottom info, downloads, settings, close-button… */}
                <div style={{ marginTop: 'auto', borderTop: '1px solid #FFFFFF33' }}>
                    {category && getActiveCategories().length > 1 && (
                        <SubMenu
                            defaultOpen
                            icon={<GiHamburgerMenu color={logoColor} size={24} />}
                            label={t('sidebar.info')}
                        >
                            {getActiveCategories().map(cat => {
                                // Just show the category if it's active, don't check for tiles/routes
                                return (
                                    <SidebarMenuItem
                                        key={cat}
                                        tooltip={t(`sidebar.categories.${cat}`)}
                                        active={category === cat}
                                        onClick={() => handleCategoryClick(cat)}
                                        icon={renderIcon(getIconForTile(cat), { color: logoColor, size: 24 })}
                                    >
                                        {t(`sidebar.categories.${cat}`)}
                                    </SidebarMenuItem>
                                );
                            })}
                        </SubMenu>
                    )}

                    {/* download manager */}
                    {(() => {
                        // Only show download manager if utility category is active for the user
                        const utilityIsActive = getActiveCategories().includes('utility');
                        const tile = translatedTiles.find(
                            tile => tile.categories.includes('utility') && routesConfig[tile.id]
                        );
                        return (utilityIsActive && tile && appRoutesConfig.Pages.DownloadManager) && (
                            <SidebarMenuItem
                                tooltip={t('tiles.downloadManager.title')}
                                active={isActiveRoute('/DownloadManager')}
                                onClick={() => {
                                    setCurrentPage(t('tiles.downloadManager.title'));
                                    navigate('/DownloadManager');
                                }}
                                icon={<CloudDownload color={logoColor} size={24} />}
                            >
                                {t('tiles.downloadManager.title')}
                            </SidebarMenuItem>
                        );
                    })()}

                    <SubMenu
                        defaultOpen
                        icon={<Help color={logoColor} size={24} />}
                        label={t('sidebar.info')}
                    >
                        <SidebarMenuItem
                            tooltip={t('sidebar.links.imprint')}
                            active={isActiveRoute('/impressum')}
                            icon={<Impressum color={logoColor} size={24} />}
                            onClick={() => window.open(impressumUrl, '_blank')}
                        >
                            {t('sidebar.links.imprint')}
                        </SidebarMenuItem>

                        <SidebarMenuItem
                            tooltip={t('sidebar.links.privacy')}
                            active={isActiveRoute('/datenschutz')}
                            icon={<Datenschutz color={logoColor} size={24} />}
                            onClick={() => window.open(datenschutzUrl, '_blank')}
                        >
                            {t('sidebar.links.privacy')}
                        </SidebarMenuItem>

                        <SidebarMenuItem
                            tooltip={t('sidebar.links.cookies')}
                            onClick={toggleCookies}
                            icon={<CookieIcon sx={{ fontSize: 24, color: 'primary.main' }} />}
                        >
                            {t('sidebar.links.cookies')}
                        </SidebarMenuItem>
                    </SubMenu>

                    <MenuCloseItem
                        icon={collapsed ? <TbChevronsRight color={logoColor} /> : <TbChevronsLeft color={logoColor} />}
                        onClick={toggleCollapse}
                    >
                        {!collapsed && <span className="fw-light mx-auto d-flex align-items-center" />}
                    </MenuCloseItem>
                </div>
            </Menu>
        </Sidebar>
    );
};

export default SidebarComponent;